(function () {
  const LAST_VISIT_KEY = 'aios:lastMeaningfulVisit';
  const SESSION_KEY = 'aios:sessionAudience';
  const PREVIOUS_VISIT_KEY = 'aios:previousVisit';

  function getAudience() {
    try {
      const existingAudience = sessionStorage.getItem(SESSION_KEY);
      const existingPrevious = Number(sessionStorage.getItem(PREVIOUS_VISIT_KEY) || 0);
      if (existingAudience) {
        return { audience: existingAudience, previousVisit: existingPrevious };
      }

      const previousVisit = Number(localStorage.getItem(LAST_VISIT_KEY) || 0);
      const sixMonths = 180 * 24 * 60 * 60 * 1000;
      const returning = previousVisit > 0 && Date.now() - previousVisit < sixMonths;
      const audience = returning ? 'returning' : 'first';

      sessionStorage.setItem(SESSION_KEY, audience);
      sessionStorage.setItem(PREVIOUS_VISIT_KEY, String(previousVisit));
      localStorage.setItem(LAST_VISIT_KEY, String(Date.now()));
      return { audience: audience, previousVisit: previousVisit };
    } catch (error) {
      return { audience: 'first', previousVisit: 0 };
    }
  }

  const visitor = getAudience();

  function bindFastInteractions() {
    const documentElement = document.documentElement;
    if (documentElement.dataset.aiosFastInteractions === 'true') return;
    documentElement.dataset.aiosFastInteractions = 'true';

    const interactiveSelector = 'a[href], button:not([disabled]), summary, label[for], [role="button"]';
    const prefetchedUrls = new Set();
    let activeTap = null;
    let suppressedTarget = null;
    let suppressUntil = 0;

    function getInteractiveTarget(eventTarget) {
      if (!(eventTarget instanceof Element)) return null;
      const target = eventTarget.closest(interactiveSelector);
      if (!target || target.matches('[aria-disabled="true"], [data-aios-no-fast-tap]')) return null;
      if (target.closest('[inert]')) return null;
      if (target.matches('[aria-controls], [data-aios-cart-open], [data-aios-quick-add], .aios-top-sale-picks__add, .aios-collection-tabs-grid__add')) return null;
      if (target.closest('#CartDrawer, .aios-global-variant-modal, .aios-cart')) return null;
      if (target instanceof HTMLAnchorElement) {
        try {
          if (/(?:^|\/)cart(?:\/|\.|$)|(?:^|\/)checkout(?:\/|$)/i.test(new URL(target.href, window.location.href).pathname)) return null;
        } catch (error) { return null; }
      }
      if (target.closest('form[action*="/cart/add"]') && target.matches('button, input[type="submit"]')) return null;
      return target;
    }

    function getInternalUrl(target) {
      if (!(target instanceof HTMLAnchorElement)) return null;
      if (!target.href || target.hasAttribute('download')) return null;
      if (target.matches('[data-no-instant], [data-no-prefetch], [rel~="nofollow"]')) return null;
      if (target.target && target.target.toLowerCase() !== '_self') return null;

      try {
        const url = new URL(target.href, window.location.href);
        if (url.origin !== window.location.origin) return null;
        if (!/^https?:$/.test(url.protocol)) return null;
        if (/(?:^|\/)cart(?:\/|\.|$)|(?:^|\/)checkout(?:\/|$)/i.test(url.pathname)) return null;
        if (url.href === window.location.href || url.hash && url.pathname === window.location.pathname && url.search === window.location.search) return null;
        return url.href;
      } catch (error) {
        return null;
      }
    }

    function prefetchTarget(target) {
      const url = getInternalUrl(target);
      if (!url || prefetchedUrls.has(url)) return;
      prefetchedUrls.add(url);

      const prefetch = document.createElement('link');
      prefetch.rel = 'prefetch';
      prefetch.href = url;
      prefetch.as = 'document';
      document.head.appendChild(prefetch);
    }

    function clearActiveTap() {
      if (activeTap?.target) activeTap.target.classList.remove('aios-fast-tap-active');
      activeTap = null;
    }

    document.addEventListener('pointerover', function (event) {
      const target = getInteractiveTarget(event.target);
      if (target) prefetchTarget(target);
    }, { capture: true, passive: true });

    document.addEventListener('focusin', function (event) {
      const target = getInteractiveTarget(event.target);
      if (target) prefetchTarget(target);
    }, true);

    document.addEventListener('pointerdown', function (event) {
      if (!event.isPrimary || event.button !== 0) return;
      const target = getInteractiveTarget(event.target);
      if (!target) return;

      clearActiveTap();
      activeTap = {
        target: target,
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        startX: event.clientX,
        startY: event.clientY,
        startedAt: Date.now(),
        cancelled: false
      };
      target.classList.add('aios-fast-tap-active');
      prefetchTarget(target);
    }, { capture: true, passive: true });

    document.addEventListener('pointermove', function (event) {
      if (!activeTap || activeTap.pointerId !== event.pointerId) return;
      const distanceX = Math.abs(event.clientX - activeTap.startX);
      const distanceY = Math.abs(event.clientY - activeTap.startY);
      if (distanceX > 12 || distanceY > 12) {
        activeTap.cancelled = true;
        activeTap.target.classList.remove('aios-fast-tap-active');
      }
    }, { capture: true, passive: true });

    document.addEventListener('pointercancel', clearActiveTap, true);

    document.addEventListener('pointerup', function (event) {
      if (!activeTap || activeTap.pointerId !== event.pointerId) return;

      const tap = activeTap;
      const isTouchTap = tap.pointerType === 'touch' || tap.pointerType === 'pen';
      const isQuickTap = Date.now() - tap.startedAt < 800;
      const releaseTarget = getInteractiveTarget(event.target);
      const sameTarget = releaseTarget === tap.target || tap.target.contains(event.target);
      clearActiveTap();

      if (!isTouchTap || !isQuickTap || tap.cancelled || !sameTarget) return;

      /*
       * Trigger the action as soon as the finger is released. Preventing the
       * compatibility click removes the old delayed-tap behaviour, while the
       * movement check above keeps horizontal/vertical swipes untouched.
       */
      event.preventDefault();
      suppressedTarget = tap.target;
      suppressUntil = Date.now() + 700;
      window.queueMicrotask(function () {
        tap.target.click();
      });
    }, { capture: true, passive: false });

    document.addEventListener('click', function (event) {
      if (!event.isTrusted || !suppressedTarget || Date.now() > suppressUntil) return;
      if (event.target === suppressedTarget || suppressedTarget.contains(event.target)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        suppressedTarget = null;
        suppressUntil = 0;
      }
    }, true);
  }

  function applyAudience() {
    const isReturning = visitor.audience === 'returning';
    document.body.classList.toggle('aios-returning', isReturning);
    document.body.classList.toggle('aios-first-visit', !isReturning);

    document.querySelectorAll('[data-aios-new-rail]').forEach(function (rail) {
      const heading = rail.querySelector('[data-aios-dynamic-heading]');
      const firstHeading = rail.dataset.firstHeading || 'New This Week';
      const returningHeading = rail.dataset.returningHeading || 'New Since Your Last Visit';
      const fallbackHeading = rail.dataset.fallbackHeading || firstHeading;
      const items = Array.from(rail.querySelectorAll('[data-aios-published-at]'));

      if (!isReturning || !visitor.previousVisit) {
        if (heading) heading.textContent = firstHeading;
        items.forEach(function (item) { item.hidden = false; });
        return;
      }

      const newlyPublished = items.filter(function (item) {
        return Number(item.dataset.aiosPublishedAt || 0) > visitor.previousVisit;
      });

      if (newlyPublished.length >= 2) {
        items.forEach(function (item) { item.hidden = newlyPublished.indexOf(item) === -1; });
        if (heading) heading.textContent = returningHeading;
      } else {
        items.forEach(function (item) { item.hidden = false; });
        if (heading) heading.textContent = fallbackHeading;
      }
    });

    if (isReturning) {
      const priority = document.querySelector('[data-aios-role="priority"]')?.closest('.shopify-section');
      const recent = document.querySelector('[data-aios-role="recently"]')?.closest('.shopify-section');
      if (priority && recent && priority.parentNode === recent.parentNode) {
        recent.insertAdjacentElement('afterend', priority);
      }
    }
  }

  function bindRailButtons(root) {
    root.querySelectorAll('[data-aios-scroll]').forEach(function (button) {
      if (button.dataset.aiosBound === 'true') return;
      button.dataset.aiosBound = 'true';
      button.addEventListener('click', function () {
        const section = button.closest('.aios-section');
        const track = section?.querySelector('[data-aios-track]');
        if (!track) return;
        const direction = button.dataset.aiosScroll === 'next' ? 1 : -1;
        track.scrollBy({ left: track.clientWidth * .82 * direction, behavior: 'smooth' });
      });
    });
  }

  function bindComparisons(root) {
    root.querySelectorAll('[data-aios-comparison]').forEach(function (comparison) {
      const range = comparison.querySelector('input[type="range"]');
      if (!range || range.dataset.aiosBound === 'true') return;
      range.dataset.aiosBound = 'true';
      const update = function () {
        comparison.style.setProperty('--aios-position', range.value + '%');
      };
      range.addEventListener('input', update);
      update();
    });
  }

  function bindHeroSliders(root) {
    root.querySelectorAll('[data-aios-hero-slider]').forEach(function (slider) {
      if (slider.dataset.aiosBound === 'true') return;
      slider.dataset.aiosBound = 'true';
      const track = slider.querySelector('[data-aios-hero-track]');
      const dots = Array.from(slider.querySelectorAll('[data-aios-hero-dot]'));
      if (!track || dots.length === 0) return;

      function setActive(index) {
        dots.forEach(function (dot, dotIndex) {
          dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
        });
      }

      dots.forEach(function (dot, index) {
        dot.addEventListener('click', function () {
          track.scrollTo({ left: track.clientWidth * index, behavior: 'smooth' });
          setActive(index);
        });
      });

      let scrollTimer;
      track.addEventListener('scroll', function () {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(function () {
          setActive(Math.round(track.scrollLeft / Math.max(track.clientWidth, 1)));
        }, 80);
      }, { passive: true });
    });
  }

  function bindHeroLinks(root) {
    root.querySelectorAll('[data-aios-hero-slider] .aios-hero-banner__button[href]').forEach(function (link) {
      if (link.dataset.aiosHeroLinkBound === 'true') return;
      link.dataset.aiosHeroLinkBound = 'true';

      link.addEventListener('click', function (event) {
        const isPrimaryClick = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
        if (!isPrimaryClick || !window.matchMedia('(max-width: 1024px)').matches) return;

        /*
         * The mobile toolbar/menu uses delegated click handlers.  Keep a hero
         * CTA tap inside the hero and navigate explicitly so it cannot also
         * be interpreted as a request to open the mobile menu.
         */
        event.preventDefault();
        event.stopImmediatePropagation();

        document.querySelectorAll('.aios-mobile-menu-toggle[id]').forEach(function (menuToggle) {
          if (!menuToggle.checked) return;
          menuToggle.checked = false;
          menuToggle.dispatchEvent(new Event('change', { bubbles: true }));
        });

        window.location.assign(link.href);
      }, true);
    });
  }

  function initialize(root) {
    bindFastInteractions();
    applyAudience();
    bindRailButtons(root || document);
    bindComparisons(root || document);
    bindHeroSliders(root || document);
    bindHeroLinks(root || document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initialize(document); });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initialize(event.target);
  });
})();
